import React, { useState, useEffect } from 'react';
import { expenseAPI, employeesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import ExportButton from '../components/ExportButton';
import { 
  Plus, 
  Receipt,
  Check,
  X,
  Clock,
  DollarSign,
  Loader2
} from 'lucide-react';

const expenseCategories = [
  'Travel',
  'Accommodation',
  'Meals',
  'Transportation',
  'Office Supplies',
  'Equipment',
  'Training',
  'Software',
  'Communication',
  'Other'
];

const currencies = ['USD', 'AED', 'INR', 'GBP', 'SGD', 'GHS', 'KES', 'UGX', 'XAF', 'MUR'];

const Expenses = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptBase64, setReceiptBase64] = useState('');
  const [extraAttachments, setExtraAttachments] = useState([]);
  const [newExpense, setNewExpense] = useState({
    employee_id: '',
    category: '',
    amount: '',
    currency: 'USD',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, employeesRes] = await Promise.all([
        expenseAPI.getAll(),
        employeesAPI.getAll()
      ]);
      
      const allExpenses = expensesRes.data;
      setExpenses(allExpenses);
      setPendingExpenses(allExpenses.filter(e => ['pending', 'manager_approved', 'hr_approved'].includes(e.status)));
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async () => {
    if (!newExpense.employee_id || !newExpense.category || !newExpense.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!receiptBase64) {
      toast.error('Receipt attachment is mandatory');
      return;
    }

    setSubmitting(true);
    try {
      await expenseAPI.create({
        ...newExpense,
        amount: parseFloat(newExpense.amount),
        receipt_base64: receiptBase64,
        attachments: extraAttachments,
      });
      toast.success('Expense claim submitted');
      setDialogOpen(false);
      setNewExpense({
        employee_id: '',
        category: '',
        amount: '',
        currency: 'USD',
        description: ''
      });
      setReceiptFile(null);
      setReceiptBase64('');
      setExtraAttachments([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Receipt size must be under 5MB');
      return;
    }
    setReceiptFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptBase64(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleExtraAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    if (valid.length < files.length) toast.error('Some files exceeded 5MB and were skipped');
    const encoded = await Promise.all(valid.map(f => new Promise((resolve) => {
      const r = new FileReader();
      r.onload = (ev) => resolve({ name: f.name, mime: f.type, content_base64: ev.target.result });
      r.readAsDataURL(f);
    })));
    setExtraAttachments([...extraAttachments, ...encoded]);
    e.target.value = '';
  };

  const handleApprove = async (expenseId, stage) => {
    try {
      await expenseAPI.approve(expenseId, stage);
      toast.success(stage ? `Expense ${stage}-approved` : 'Expense approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve expense');
    }
  };

  const handleReject = async (expenseId) => {
    try {
      await expenseAPI.reject(expenseId);
      toast.success('Expense rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject expense');
    }
  };

  const handleMarkPaid = async (expenseId) => {
    try {
      await expenseAPI.markPaid(expenseId);
      toast.success('Marked as paid');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  // Decide which approval action is appropriate given current user role + expense status
  const getNextApprovalStage = (expense) => {
    if (!user) return null;
    const status = expense.status;
    if (['rejected', 'paid', 'approved', 'finance_approved'].includes(status)) return null;
    if (user.role === 'manager' && status === 'pending') return 'manager';
    if (user.role === 'hr_admin' && ['pending', 'manager_approved'].includes(status)) return 'hr';
    if (user.role === 'payroll_admin' && ['hr_approved', 'manager_approved', 'pending'].includes(status)) return 'finance';
    if (user.role === 'super_admin') {
      if (status === 'pending') return 'manager';
      if (status === 'manager_approved') return 'hr';
      if (status === 'hr_approved') return 'finance';
    }
    return null;
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'approved':
      case 'finance_approved':
      case 'hr_approved':
        return 'default';
      case 'manager_approved': return 'secondary';
      case 'rejected': return 'destructive';
      case 'paid': return 'outline';
      default: return 'secondary';
    }
  };

  const STATUS_LABEL = {
    pending: 'Pending',
    manager_approved: 'Manager ✓',
    hr_approved: 'HR ✓',
    finance_approved: 'Finance ✓',
    approved: 'Approved',
    rejected: 'Rejected',
    paid: 'Paid',
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const canApprove = ['super_admin', 'hr_admin', 'manager', 'payroll_admin'].includes(user?.role);

  const totalPending = pendingExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalApproved = expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Expense Claims</h1>
          <p className="text-muted-foreground">Submit and manage expense reimbursements</p>
        </div>
        <div className="flex gap-2">
        <ExportButton endpoint="/reports/expenses/export" filename="expenses" testId="export-expenses" />
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-hover" data-testid="submit-expense-btn">
              <Plus size={18} className="mr-2" />
              Submit Expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit Expense Claim</DialogTitle>
              <DialogDescription>
                Submit a new expense for reimbursement
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select 
                  value={newExpense.employee_id} 
                  onValueChange={(v) => setNewExpense({ ...newExpense, employee_id: v })}
                >
                  <SelectTrigger data-testid="expense-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={newExpense.category} 
                  onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}
                >
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    data-testid="expense-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select 
                    value={newExpense.currency} 
                    onValueChange={(v) => setNewExpense({ ...newExpense, currency: v })}
                  >
                    <SelectTrigger data-testid="expense-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map(cur => (
                        <SelectItem key={cur} value={cur}>
                          {cur}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe the expense..."
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  data-testid="expense-description"
                />
              </div>

              <div className="space-y-2">
                <Label>Receipt Attachment <span className="text-destructive">*</span> <span className="text-xs text-muted-foreground">(Required, max 5MB)</span></Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleReceiptChange}
                  data-testid="expense-receipt-input"
                />
                {receiptFile && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400" data-testid="receipt-attached">
                    ✓ Attached: {receiptFile.name} ({(receiptFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
                {!receiptFile && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">⚠ Receipt is mandatory to submit a claim</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Additional Attachments <span className="text-xs text-muted-foreground">(Optional, max 5MB each)</span></Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleExtraAttachments}
                  data-testid="expense-extra-attachments"
                />
                {extraAttachments.length > 0 && (
                  <ul className="text-xs space-y-1 mt-1" data-testid="extra-attached-list">
                    {extraAttachments.map((a, i) => (
                      <li key={i} className="flex items-center justify-between bg-muted/30 p-1.5 rounded">
                        <span className="truncate">📎 {a.name}</span>
                        <button type="button" className="text-destructive ml-2" onClick={() => setExtraAttachments(extraAttachments.filter((_, idx) => idx !== i))}>×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateExpense} disabled={submitting} data-testid="confirm-expense-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit Claim
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Receipt className="text-primary" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Claims</p>
                <p className="text-2xl font-bold">{expenses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Clock className="text-amber-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{pendingExpenses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <DollarSign className="text-red-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Amount</p>
                <p className="text-2xl font-bold">${totalPending.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="text-emerald-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">${totalApproved.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="expense-tab-pending">
            Pending ({pendingExpenses.length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="expense-tab-all">
            All Claims
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Date</TableHead>
                    {canApprove && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingExpenses.length > 0 ? (
                    pendingExpenses.map((expense) => {
                      const nextStage = getNextApprovalStage(expense);
                      return (
                      <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                        <TableCell className="font-medium">
                          {getEmployeeName(expense.employee_id)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{expense.category}</Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(expense.amount, expense.currency)}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {expense.description}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(expense.status)}>
                            {STATUS_LABEL[expense.status] || expense.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(expense.created_at).toLocaleDateString()}
                        </TableCell>
                        {canApprove && (
                          <TableCell>
                            <div className="flex gap-2">
                              {nextStage && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-emerald-600 hover:bg-emerald-50"
                                  onClick={() => handleApprove(expense.id, nextStage)}
                                  data-testid={`approve-expense-${expense.id}`}
                                  title={`Approve as ${nextStage}`}
                                >
                                  <Check size={16} className="mr-1" />
                                  <span className="text-xs">{nextStage}</span>
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleReject(expense.id)}
                                data-testid={`reject-expense-${expense.id}`}
                              >
                                <X size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={canApprove ? 7 : 6} className="text-center py-8 text-muted-foreground">
                        No pending expense claims
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.length > 0 ? (
                    expenses.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell className="font-medium">
                          {getEmployeeName(expense.employee_id)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{expense.category}</Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(expense.amount, expense.currency)}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {expense.description}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(expense.status)}>
                            {STATUS_LABEL[expense.status] || expense.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(expense.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No expense claims found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Expenses;
